import PropTypes from 'prop-types';
import Container from '@/components/Container';
import Image from '@/components/Image/RemoteImage';
import Link from '@/components/Link';
import DownloadProgram from '@/domain/DownloadProgram';

const defaultProps = {
  program: null,
  programAskInfoToDownload: false,
  programDetails: {},
};

const propTypes = {
  heroCopy: PropTypes.node.isRequired,
  heroLead: PropTypes.node.isRequired,
  heroImage: PropTypes.string.isRequired,
  heroImageAlt: PropTypes.string.isRequired,
  program: PropTypes.object,
  programAskInfoToDownload: PropTypes.bool,
  programDetails: PropTypes.shape({
    programHsPortalId: PropTypes.string.isRequired,
    programHsFormId: PropTypes.string.isRequired,
    programModalDescription: PropTypes.string.isRequired,
    programModalTitle: PropTypes.string.isRequired,
    programTestimonial: PropTypes.object,
  }),
};

export default function MarketingHero({
  heroCopy,
  heroLead,
  heroImage,
  heroImageAlt,
  program,
  programAskInfoToDownload,
  programDetails: {
    programHsPortalId,
    programHsFormId,
    programModalDescription,
    programModalTitle,
    programTestimonial,
  },
}) {
  const hasDownloadProgram = program !== null;

  const content = (
    <div className="md:py-10 lg:py-20 xl:py-40 relative bg-gradient-to-t from-green-200 to-white overflow-hidden">
      <Container className="relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-8 space-y-8 lg:space-y-10 lg:col-start-2 lg:-ml-10 py-12 md:py-0">
            <div className="space-y-4 grid grid-cols-1 lg:grid-cols-12 space-y-6">
              <h1 className="text-gray-900 lg:col-span-12 leading-tight text-4xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight">
                {heroCopy}
              </h1>
              <div className="lg:col-span-9 xl:col-span-10 xl:pr-10 text-lg lg:text-xl text-gray-800">
                {heroLead}
              </div>
            </div>
            <div className="relative">
              <div className="flex space-y-6 md:space-y-0 flex-col md:flex-row md:items-center md:space-x-6">
                <Link className="rounded-full inline-fex bg-green-500 text-white font-semibold px-7 py-4 leading-4 text-lg focus:outline-none hover:bg-green-600 focus:bg-green-600 focus:ring-2 focus:ring-green-600 transition ease-in-out duration-100 hover:shadow-xl focus:shadow-none text-center" href="/clases">
                  Inscríbete hoy mismo
                </Link>
                {(hasDownloadProgram && programAskInfoToDownload) && (
                  <DownloadProgram.Link>
                    Descargar programa
                  </DownloadProgram.Link>
                )}
                {(hasDownloadProgram && !programAskInfoToDownload) && (
                  <a href={program.guid} className="font-bold text-lg text-gray-900 hover:text-gray-700 text-center" target="_blank" rel="noopener noreferrer" download>
                    Descargar programa
                  </a>
                )}
              </div>
              <div className="absolute left-0 top-0 -ml-32 mt-10">
                <svg xmlns="http://www.w3.org/2000/svg" width="120" height="35">
                  <g className="stroke-current text-gray-900" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeMiterlimit="10">
                    <path d="M2.604 11.713C.493 23.5 14.752 38.096 25.349 28.298c4.057-3.752 6.124-12.578 11.736-14.328 5.07-1.582 10.508 3.462 14.03 6.42 3.312 2.783 6.481 7.043 11.264 6.274 5.658-.909 7.922-7.63 12.645-9.502 3.914-1.551 1.654.373 5.418 2.064 8.492 3.815 26.078-8.708 32.912-12.264"></path>
                    <path d="M113.166 14.662c.126-4.386 3.5-7.089 4.44-11.18-3.141.627-9.566 1.613-12.685.331"></path>
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </Container>
      <div className="pointer-events-none max-h-full lg:absolute lg:h-full lg:inset-0 lg:mt-2 lg:grid lg:grid-cols-12 lg:items-end">
        <div className="relative z-10 max-h-full lg:h-full lg:col-span-5 lg:col-start-8 flex">
          <Image
            src={heroImage}
            className="blocklg:h-full flex select-none pointer-events-none"
            alt={heroImageAlt}
            width={720}
            height={800}
          />
        </div>
        <div className="absolute top-0 bottom-0 right-0 w-full z-0 hidden lg:block">
          <div className="w-2/5 bg-gradient-to-l from-green-500 to-transparent top-0 bottom-0 right-0 absolute z-10 mt-20 rounded-tl-lg transform -skew-y-6" />
          <div className="w-2/4 bg-gradient-to-l from-green-500 to-transparent top-0 bottom-0 right-0 absolute z-20 mt-40 -mb-20 rounded-tl-lg transform -skew-y-6" />
          <div className="w-2/6 bg-gradient-to-l from-green-600 to-transparent top-0 bottom-0 right-0 absolute z-30 mt-80 mb-10 rounded-tl-lg transform -skew-y-6 opacity-50" />
        </div>
      </div>
    </div>
  );

  if (hasDownloadProgram && programAskInfoToDownload) {
    return (
      <DownloadProgram.Provider
        formUrl={programHsFormId}
        portalId={programHsPortalId}
        programUrl={program.guid}
        trainingName={heroCopy}
        modalDescription={programModalDescription}
        modalTitle={programModalTitle}
        testimonial={programTestimonial}
      >
        {content}
        <DownloadProgram.Modal />
      </DownloadProgram.Provider>
    );
  }

  return content;
}

MarketingHero.defaultProps = defaultProps;
MarketingHero.propTypes = propTypes;
